import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'shared/_ui';
import {
  InstructionBlock,
  InstructionClient,
  InstructionItem,
  resolveValue,
  safeDeeplink,
  SetupValues,
} from '../model/instructions';
import styles from './styles.module.scss';

const renderText = (value = '') =>
  value
    .split('**')
    .map((part, index) =>
      index % 2 ? <strong key={index}>{part}</strong> : part
    );
const Parameter: React.FC<{ item: InstructionItem; values: SetupValues }> = ({
  item,
  values,
}) => {
  const { t } = useTranslation();
  const [shown, setShown] = useState(false);
  const [copyState, setCopyState] = useState('');
  const value = resolveValue(item, values);
  const copy = async () => {
    if (value === undefined) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopyState('copied');
    } catch {
      setCopyState('copyFailed');
    }
  };
  return (
    <div className={styles.parameter}>
      <dt>{item.label}</dt>
      <dd>
        <span className={styles.parameterValue}>
          {value === undefined
            ? t('setup.expanded.unavailable')
            : item.secret && !shown
            ? '••••••••'
            : value}
        </span>
        <div className={styles.parameterActions}>
          {item.secret && value !== undefined && (
            <button
              type="button"
              aria-label={t(`setup.expanded.${shown ? 'hide' : 'show'}`, {
                label: item.label,
              })}
              onClick={() => setShown(!shown)}
            >
              {t(`setup.expanded.${shown ? 'hideValue' : 'showValue'}`)}
            </button>
          )}
          {value !== undefined && (
            <button
              type="button"
              aria-label={t('setup.expanded.copy', { label: item.label })}
              onClick={copy}
            >
              {t('setup.expanded.copyValue')}
            </button>
          )}
        </div>
        {copyState && (
          <span role="status">{t(`setup.expanded.${copyState}`)}</span>
        )}
      </dd>
    </div>
  );
};

type Props = {
  blocks: InstructionBlock[];
  client: InstructionClient;
  values: SetupValues;
  configKinds: string[];
  download: (kind: string) => void;
  downloading: boolean;
};
export const InstructionBlocks: React.FC<Props> = ({ blocks, ...props }) => {
  const { t } = useTranslation();
  return (
    <>
      {blocks.map((block, index) => {
        switch (block.type) {
          case 'muted':
            return null;
          case 'sources':
            return (
              <div key={index} className={styles.block}>
                {block.lead && <p>{renderText(block.lead)}</p>}
                {props.client.sources.map((source, sourceIndex) => (
                  <div key={sourceIndex} className={styles.block}>
                    <Button
                      type="secondary"
                      onClick={() =>
                        window.open(source.url, '_blank', 'noopener,noreferrer')
                      }
                    >
                      {source.label}
                    </Button>
                    {source.version && (
                      <p>{t('setup.version', { version: source.version })}</p>
                    )}
                    {source.note && <p>{renderText(source.note)}</p>}
                  </div>
                ))}
              </div>
            );
          case 'download': {
            const kind = block.artifact ?? props.client.configKind;
            return (
              <Button
                key={index}
                type="primary"
                disabled={!kind || !props.configKinds.includes(kind)}
                loading={props.downloading}
                onClick={() => kind && props.download(kind)}
              >
                {block.label ?? t('setup.downloadConfig')}
              </Button>
            );
          }
          case 'steps':
            return (
              <ol key={index} className={styles.steps}>
                {block.items
                  .filter((item) => item.text)
                  .map((item, itemIndex) => (
                    <li key={itemIndex}>{renderText(item.text)}</li>
                  ))}
              </ol>
            );
          case 'params':
            return (
              <dl key={index} className={styles.parameters}>
                {block.items.map((item, itemIndex) => (
                  <Parameter
                    key={itemIndex}
                    item={item}
                    values={props.values}
                  />
                ))}
              </dl>
            );
          case 'deeplink': {
            const url = resolveValue(block, props.values);
            return (
              <div key={index} className={styles.block}>
                <Button
                  type="secondary"
                  disabled={!safeDeeplink(url)}
                  onClick={() => {
                    if (safeDeeplink(url)) window.location.assign(url);
                  }}
                >
                  {block.label ?? t('setup.expanded.openApp')}
                </Button>
                {!safeDeeplink(url) && <p>{t('setup.expanded.unavailable')}</p>}
                {block.fallback && <p>{renderText(block.fallback)}</p>}
              </div>
            );
          }
          case 'details':
            return (
              <details key={index}>
                <summary>{renderText(block.summary)}</summary>
                <InstructionBlocks blocks={block.blocks} {...props} />
              </details>
            );
          case 'subheading':
            return <h4 key={index}>{renderText(block.content)}</h4>;
          case 'notice':
          case 'warning':
            return (
              <div key={index} className={styles.notice}>
                {renderText(block.content)}
              </div>
            );
          default:
            return <p key={index}>{renderText(block.content)}</p>;
        }
      })}
    </>
  );
};
